import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Collapse,
  Divider,
  Alert,
  Chip,
  Stack,
  Card,
  CardContent,
  Button
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as AdminIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { useDesigner } from '../../contexts/DesignerContext';
import { useAuth } from '../../contexts/AuthContext';

interface AdminControlsProps {
  expanded?: boolean;
}

const AdminControls: React.FC<AdminControlsProps> = ({ expanded = false }) => {
  const { user, isAdmin } = useAuth();
  const { 
    selections, 
    setGarmentMaskPrompt, 
    setHumanMaskPrompt, 
    setPositivePrompt,
    setSeed
  } = useDesigner();
  
  const [isExpanded, setIsExpanded] = useState(expanded);
  const [showPreview, setShowPreview] = useState(false);
  const [configSeed, setConfigSeed] = useState<string>('Loading...');

  // Only show for admin users
  if (!user || !isAdmin) {
    return null;
  }

  // Fetch seed configuration from backend
  useEffect(() => {
    const fetchConfigSeed = async () => {
      try {
        // Create a simple payload to get seed info
        const response = await fetch('/api/get_seed_config', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: user?.username || 'fashionbabe',
            model: selections.model
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          setConfigSeed(data.seed || 'Not configured');
        } else {
          setConfigSeed('Error loading config');
        }
      } catch (error) {
        console.error('Error fetching seed config:', error);
        setConfigSeed('Error loading config');
      }
    };

    if (user && isAdmin) {
      fetchConfigSeed();
    }
  }, [user, isAdmin, selections.model]);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const parameterInfo = [
    {
      label: 'Seed',
      value: selections.seed || '',
      setter: setSeed,
      description: 'Seed for reproducible image generation. Leave empty to use config value.',
      placeholder: `Config value: ${configSeed}`,
      comfyNode: 'KSampler',
      readonly: false
    },
    {
      label: 'Garment Mask Prompt',
      value: selections.garmentMaskPrompt,
      setter: setGarmentMaskPrompt,
      description: 'Controls what garment parts are masked in ComfyUI GroundingDinoSAMSegment(GarmentMask)',
      placeholder: 'e.g., body, t-shirt, dress, pants',
      comfyNode: 'GroundingDinoSAMSegment(segment anything)-GarmentMask'
    },
    {
      label: 'Human Mask Prompt',
      value: selections.humanMaskPrompt,
      setter: setHumanMaskPrompt,
      description: 'Controls what human parts are masked in ComfyUI GroundingDinoSAMSegment(HumanMask)',
      placeholder: 'e.g., body, person, human',
      comfyNode: 'GroundingDinoSAMSegment(segment anything)-HumanMask'
    },
    {
      label: 'Positive Prompt',
      value: selections.positivePrompt,
      setter: setPositivePrompt,
      description: 'Additional positive prompt for image generation in ComfyUI CLIP-Text-Encode',
      placeholder: 'e.g., high quality, professional photo, good lighting',
      comfyNode: 'CLIP-Text-Encode-Positive-Prompt'
    }
  ];

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        mb: 2, 
        border: '1px solid',
        borderColor: 'warning.main',
        bgcolor: 'warning.50'
      }}
    >
      <Box
        onClick={handleToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 2,
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'warning.100'
          }
        }}
      >
        <AdminIcon color="warning" sx={{ mr: 1 }} />
        <Typography variant="h6" sx={{ flexGrow: 1, color: 'warning.dark' }}>
          Admin Controls - Advanced Parameters
        </Typography>
        <IconButton size="small">
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={isExpanded}>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              These parameters directly control ComfyUI workflow nodes. 
              Regular users see simplified options like "Transfer wyglądu".
            </Typography>
          </Alert>


          <Stack spacing={3}>
            {parameterInfo.map((param, index) => (
              <Box key={index}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  {param.label}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {param.description}
                </Typography>
                
                <Chip 
                  label={`ComfyUI Node: ${param.comfyNode}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
                
                <TextField
                  fullWidth
                  value={param.value}
                  onChange={(e) => param.setter(e.target.value)}
                  placeholder={param.placeholder}
                  variant="outlined"
                  size="small"
                  helperText={
                    param.label === 'Seed' && !param.value 
                      ? `Will use config value: ${configSeed}` 
                      : `Current: "${param.value || 'empty'}"`
                  }
                />
              </Box>
            ))}
          </Stack>

          <Divider sx={{ my: 2 }} />
          
          <Alert severity="warning">
            <Typography variant="body2">
              <strong>Admin Only:</strong> These controls modify low-level ComfyUI parameters. 
              Changes affect image generation behavior immediately.
            </Typography>
          </Alert>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default AdminControls;